/**
 * #644: how long a sent Main Command turn keeps watching for its selected recipients' replies.
 *
 * After Send, the page rereads only the bounded Main Command rows (not the whole room) until
 * every selected recipient has replied or settled. Local Observer runtimes answer UX then Core
 * in turn and live replies took up to ~2.5 minutes, so a fixed ~20 second watch left the owner
 * waiting on the 30-second room poll with no visible pending -> replied change. The watch now
 * keeps its fast phase, then continues at a slower bounded cadence while a reply is still
 * pending, and always stops.
 */

/** Rereads in the fast phase, one second apart. */
export const MAIN_COMMAND_FAST_REREADS = 20;
export const MAIN_COMMAND_FAST_REREAD_MS = 1_000;
/** After the fast phase, reread this often while a selected recipient is still pending. */
export const MAIN_COMMAND_SLOW_REREAD_MS = 3_000;
/** A turn is never watched longer than this; the room poll covers anything later. */
export const MAIN_COMMAND_REPLY_WATCH_MS = 6 * 60_000;

/**
 * The delay before the next targeted reread, or null to stop watching.
 * `attempt` counts rereads already made; `elapsedMs` is time since the turn was accepted.
 */
export function mainCommandNextRereadMs(input: {
  readonly attempt: number;
  readonly elapsedMs: number;
  readonly settled: boolean;
}): number | null {
  if (input.settled) return null;
  if (input.attempt < MAIN_COMMAND_FAST_REREADS) return MAIN_COMMAND_FAST_REREAD_MS;
  if (input.elapsedMs + MAIN_COMMAND_SLOW_REREAD_MS > MAIN_COMMAND_REPLY_WATCH_MS) return null;
  return MAIN_COMMAND_SLOW_REREAD_MS;
}
